import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Skeleton, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useQuery } from '@apollo/client'

import { queries } from '../../graphql'
import { BlockQuote, Button, Collapse, Fields, Icons, InputAdornment, Link, MenuItem, Row, Stack, Text } from '..'
import { titleCase } from '../../utils'

export function Select({ register, fundId }) {
  const { data, loading } = useQuery(queries.paymentMethods.myLinkedBanks, {
    skip: !fundId,
    variables: { fundId },
  })
  const [isNew, setIsNew] = useState(false)
  const linkedBanks = data?.me?.fund?.linkedBanks

  useEffect(() => !loading && !linkedBanks && setIsNew(true), [loading, linkedBanks])

  if (loading) return <Skeleton width="30rem" height="56px" />

  if (!linkedBanks?.length) {
    return (
      <Text.Body>
        No bank accounts linked. <Link to={`/profile/${fundId}/payment-methods`}>Add a bank account</Link> to use bank
        transfer.
      </Text.Body>
    )
  }

  return <SavedAccounts register={register} setIsNewBankAccount={setIsNew} linkedBanks={linkedBanks} />
}

export function FAQ() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <BlockQuote sx={{ pt: 1, pb: 1 }}>
      <Row alignItems="center">
        <Button unstyled onClick={() => setIsOpen(!isOpen)}>
          <Text.Bold>FAQs</Text.Bold>
          {isOpen ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
        </Button>
      </Row>
      <Collapse in={isOpen} unmountOnExit sx={{ mt: '0 !important' }}>
        <Text.Body>
          <ul style={{ listStyle: 'none', lineHeight: 2 }}>
            <li>
              <b>Does GiveWise store any banking information?</b> No, GiveWise does not store any banking information.
              GiveWise uses a banking platform called ZumRails which allows you to contribute to your fund directly from
              your linked bank accounts.
            </li>
            <li>
              <b>Why are my contributions showing in &ldquo;Pending&rdquo; state?</b> When you make a contribution
              directly from one of your linked bank accounts, you are beginning the transfer of funds. Banks will take
              some time to verify these transactions. When the transaction is verified by the bank, it will be be marked
              as &ldquo;Cleared&rdquo;, or &ldquo;Cancelled&rdquo; / &ldquo;Failed&rdquo; if the transaction could not
              be verified.
            </li>
            <li>
              <b>Can I use funds that are in the &ldquo;Pending&rdquo; state?</b> No. Once the contribution is marked in
              the &ldquo;Cleared&rdquo; state, the funds will be available in your Giving Wallet, will appear on the Tax
              Receipt, and will be available to gift to charities.
            </li>
            <li>
              <b>How long will my contribution take to clear the bank?</b> Your contribution will take up to 4 business
              days.
            </li>
          </ul>
        </Text.Body>
      </Collapse>
    </BlockQuote>
  )
}

export function SavedAccounts({ register, linkedBanks, setIsNewBankAccount }) {
  const { fundId } = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [valueAs, _setValueAs] = useState(linkedBanks.length ? linkedBanks[0].id : '')
  const setValueAs = (text) => {
    const v = parseInt(text, 10)
    _setValueAs(v)
    return v
  }
  const onChange = ({ target: { value } }) => setValueAs(value)

  return (
    <Row justifyContent="space-between" alignItems="flex-start">
      <Fields.Text
        id="outlined-select-currency"
        select
        label="Bank Account"
        sx={{ maxWidth: '30rem' }}
        {...register('paymentMethodId', { setValueAs, onChange })}
        value={valueAs}
        defaultValue={valueAs}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icons.Account />
            </InputAdornment>
          ),
        }}
      >
        {linkedBanks.map(({ id, details: { institution, last4 } }) => (
          <MenuItem key={id} value={id}>
            {titleCase(institution)}: ****{last4}
          </MenuItem>
        ))}
      </Fields.Text>

      <Stack alignItems={`flex-${isMobile ? 'start' : 'end'}`}>
        <Stack width="auto">
          <Button variant="text" to={`/profile/${fundId}/payment-methods`}>
            Manage Bank Accounts
          </Button>
        </Stack>
      </Stack>
    </Row>
  )
}
